import { getDoc, runTransaction } from 'firebase/firestore';
import { db, docRef } from '../firebase/config';
import { COLLECTIONS } from '../utils/constants';
import { normalizeEmailOrPhone } from '../utils/emailUtils';

export class IdentityTakenError extends Error {
  constructor(value) {
    super('IDENTITY_TAKEN');
    this.name = 'IdentityTakenError';
    this.code = 'identity-taken';
    this.value = value;
  }
}

export function toIdentityId(raw) {
  return normalizeEmailOrPhone(raw);
}

export function extractIdentityValues(data = {}) {
  const values = new Set();
  const add = (v) => {
    const id = toIdentityId(v);
    if (id) values.add(id);
  };
  add(data.emailOrPhone);
  add(data.phoneNumber);
  add(data.email);
  add(data.phone);
  return [...values];
}

export function isIdentityTakenError(err) {
  return (
    err?.code === 'identity-taken' ||
    err?.message === 'IDENTITY_TAKEN' ||
    err?.name === 'IdentityTakenError'
  );
}

export function describeIdentityError(err) {
  if (isIdentityTakenError(err)) {
    return 'Bu e-posta veya telefon başka bir hesapta kullanılıyor.';
  }
  return null;
}

function identityRef(identityId) {
  return docRef(COLLECTIONS.IDENTITIES, identityId);
}

function kindFor(identityId) {
  return String(identityId).includes('@') ? 'email' : 'phone';
}

/** Başka hesapta kayıtlı mı? ownerId verilirse kendi kaydı serbest. */
export async function assertIdentitiesAvailable(values, ownerId = null) {
  const ids = [...new Set((values || []).map(toIdentityId).filter(Boolean))];
  await Promise.all(
    ids.map(async (id) => {
      const snap = await getDoc(identityRef(id));
      if (snap.exists() && snap.data()?.ownerId !== ownerId) {
        throw new IdentityTakenError(id);
      }
    })
  );
}

/**
 * Transaction içinde kimlikleri rezerve / serbest bırak.
 * Tüm get() çağrıları yazmadan önce yapılmalı.
 */
export async function syncIdentitiesInTx(tx, ownerId, { claim = [], release = [] } = {}) {
  if (!ownerId) throw new Error('INVALID_OWNER');

  const claimIds = [...new Set((claim || []).map(toIdentityId).filter(Boolean))];
  const releaseIds = [...new Set((release || []).map(toIdentityId).filter(Boolean))]
    .filter((id) => !claimIds.includes(id));

  const claimReads = [];
  for (const id of claimIds) {
    // eslint-disable-next-line no-await-in-loop
    const snap = await tx.get(identityRef(id));
    claimReads.push({ id, snap });
  }
  const releaseReads = [];
  for (const id of releaseIds) {
    // eslint-disable-next-line no-await-in-loop
    const snap = await tx.get(identityRef(id));
    releaseReads.push({ id, snap });
  }

  for (const { id, snap } of claimReads) {
    if (snap.exists() && snap.data()?.ownerId !== ownerId) {
      throw new IdentityTakenError(id);
    }
  }

  const now = Date.now();
  for (const { id, snap } of releaseReads) {
    if (snap.exists() && snap.data()?.ownerId === ownerId) {
      tx.delete(identityRef(id));
    }
  }
  for (const id of claimIds) {
    tx.set(identityRef(id), {
      ownerId,
      kind: kindFor(id),
      value: id,
      updatedAt: now,
    });
  }
}

/** Profil yazımından bağımsız kimlik senkronu */
export async function syncIdentities(ownerId, { claim = [], release = [] } = {}) {
  await runTransaction(db, async (tx) => {
    await syncIdentitiesInTx(tx, ownerId, { claim, release });
  });
}

/** Hesap silinince e-posta/telefonu serbest bırak */
export async function releaseIdentities(ownerId, values) {
  const ids = [...new Set((values || []).map(toIdentityId).filter(Boolean))];
  if (!ownerId || ids.length === 0) return;
  await runTransaction(db, async (tx) => {
    const reads = [];
    for (const id of ids) {
      // eslint-disable-next-line no-await-in-loop
      const snap = await tx.get(identityRef(id));
      reads.push({ id, snap });
    }
    for (const { id, snap } of reads) {
      if (snap.exists() && snap.data()?.ownerId === ownerId) {
        tx.delete(identityRef(id));
      }
    }
  });
}
